// vacancyPdfExporter.ts
import { toPng } from "html-to-image";
import PDFExporter from "@/modules/pdf-export/PDFExporter";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import ObjectService from "@/apps/tatar/objectsApp/ObjectService";
import { HTTP } from "@/utils/Http";
import CDNService, { CDNGenerateDownloadLink } from "@/services/CDNService";
import { AssetTypes } from "@/model/AssetTypes";
import StringUtils from "@/utils/StringUtils";
import { APActivity } from "../../../ActivityInterfaces";
import {
  ActionFlowEntry,
  ActionFlowEntryPublicData,
} from "@/modules/action-flow/model/action-flow.interface";
import { store } from "@/redux/store";

const getObjectAddress = (object: any): string => {
  const addressObj = object.data.feature.address?.address.find(
    (a: any) => a.isMain
  );
  if (addressObj) {
    return `${addressObj.streetname} ${addressObj.streetnumber}, ${addressObj.postalcode} ${addressObj.city}`;
  }
  return "";
};

const getBase64FromUrl = async (url: string): Promise<string> => {
  const data = await fetch(url);
  const blob = await data.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getObjectImage = async (object: any): Promise<string> => {
  try {
    const image = object.data.images[0];
    const cdnObj = object.cdn.find((cdn: any) => cdn._id === image.linkToCdn);
    const param: CDNGenerateDownloadLink = {
      assetField: "data.images",
      assetId: object._id,
      assetType: AssetTypes.Portfolio.Object,
      cdnId: cdnObj._id,
      fileKey: cdnObj.key,
      hasFolderReadPermissions: true,
    };
    const cdnLink = CDNService.fetchCDNLink(param);
    return cdnLink;
  } catch (error) {
    return "";
  }
};

const fetchRentalUnit = async (activity: any): Promise<any> => {
  try {
    const rentalUnitInfo = activity.data.relations[0];
    if (!rentalUnitInfo) {
      return null;
    }
    const result = await HTTP.get({
      url: `/api/asset/${rentalUnitInfo.assetType}/${rentalUnitInfo.assetId}`,
      target: "EMPTY",
    });
    return result;
  } catch (error) {
    throw new Error("Error fetching rental unit");
  }
};

export const createVacancyApplicantActionFlowPDF = async (params: {
  activity: APActivity;
  offerActionFlowEntry: ActionFlowEntry;
  qrReference: React.RefObject<any>;
}): Promise<void> => {
  try {
    const { activity, offerActionFlowEntry, qrReference } = params;

    const activeApplication = store.getState().uiConfig.activeApplication;

    const qr = await toPng(qrReference.current);
    const entryData = offerActionFlowEntry.data as ActionFlowEntryPublicData;

    const object = await ObjectService.fetchObjectById(activity.data.objectId);
    const address = getObjectAddress(object);

    const logoUrl = activeApplication.logo.navbar.replace("white", "logo");
    const logoBase64 = await getBase64FromUrl(logoUrl);

    const imageUrl = await getObjectImage(object);
    const imageBase64 = imageUrl !== "" ? await getBase64FromUrl(imageUrl) : "";

    const rentalUnit = await fetchRentalUnit(activity);

    // TODO SJ ADD TRANSLATION
    const docDefinition: TDocumentDefinitions = {
      content: [
        // Header with Logo and Title
        {
          columns: [
            {
              image: logoBase64,
              width: 60,
              alignment: "left",
            },
            {
              text: `Verfügbare Mietwohnung`,
              style: "header",
              alignment: "center",
            },
          ],
        },
        {
          text: address,
          style: "subheader",
          alignment: "center",
          margin: [0, 10, 0, 10],
        },
        // Property Overview and Rental Unit Details
        {
          columns: [
            imageBase64
              ? {
                  image: imageBase64,
                  width: 250,
                }
              : null,
            {
              stack: [
                { text: "Wohnungsdetails:", style: "sectionHeader" },
                { text: `Wohnung: ${rentalUnit?.data.displayName || "-"}` },
                { text: `Wohnfläche: ${activity.data.meta?.area || 0} m²` },
                {
                  text: `Miete: ${StringUtils.formatCurrency(
                    activity.data.meta?.planRent || 0
                  )}`,
                },
              ],
              margin: [20, 0, 0, 0],
            },
          ],
        },
        { text: "\n" },
        // QR Code Section
        {
          text: "Scannen Sie den QR-Code für weitere Informationen und zur Selbstauskunft.",
          style: "subheader",
          margin: [0, 20, 0, 5],
        },
        {
          image: qr,
          width: 150,
          alignment: "center",
        },
        { text: "\n" },
        // Contact Information (token)
        {
          text: `Code: ${entryData.token}`,
          style: "subheader",
          alignment: "center",
        },
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 10, 0, 10],
        },
        subheader: {
          fontSize: 14,
          bold: true,
          margin: [0, 10, 0, 5],
        },
        sectionHeader: {
          fontSize: 12,
          bold: true,
          margin: [0, 10, 0, 5],
        },
        contactInfo: {
          fontSize: 10,
          margin: [0, 5, 0, 5],
        },
      },
    };

    PDFExporter.exportPdf(`${activity.data.displayName}.pdf`, docDefinition);
  } catch (error) {
    console.error("Error exporting PDF: ", error);
    throw error;
  }
};
